import { Table, TableColumnType, TableState } from '@app/ui';
import { useUrlTableState } from '@app/common/utils';
import { useUserContextSelector } from '@app/main/utils';
import {
  AppointmentRequestStatus,
  AppointmentRequestListAction,
  AppointmentRequestListWidgetProps,
  useAppointmentRequestListColumns,
} from '@mod/schedule';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PatientPortalAppointmentRequest,
  PatientPortalAppointmentRequestVisitorRef,
  useGetPatientPortalAppointmentRequestsQuery,
} from '@app/medical-record/api';

function getCurrentUserVisitorRef(
  item: PatientPortalAppointmentRequest,
  userId: string,
): PatientPortalAppointmentRequestVisitorRef | null {
  return item.visitorRefs.find((visitorRef) => visitorRef.visitorId === userId) ?? null;
}

function usePatientPortalAppointmentRequestColumns(
  columnsSettings: AppointmentRequestListWidgetProps['columns'],
  actions: AppointmentRequestListAction[],
  userId: string,
) {
  const baseColumns = useAppointmentRequestListColumns(
    typeof columnsSettings === 'function' ? { visitor: false } : { ...columnsSettings, visitor: false },
    actions,
  ) as TableColumnType<PatientPortalAppointmentRequest>[];

  const columns = useMemo(
    () =>
      typeof columnsSettings === 'function'
        ? (columnsSettings(baseColumns as never) as TableColumnType<PatientPortalAppointmentRequest>[])
        : baseColumns,
    [baseColumns, columnsSettings],
  );

  return useMemo(
    () =>
      columns
        .filter((column) => column.key !== 'visitor')
        .map((column) => {
        if (column.key !== 'type') {
          return column;
        }

        const filterable =
          column.filterable && typeof column.filterable === 'object'
            ? {
                ...column.filterable,
                by: (item: PatientPortalAppointmentRequest) =>
                  getCurrentUserVisitorRef(item, userId)?.appointmentType?.name ?? null,
              }
            : column.filterable;

        return {
          ...column,
          filterable,
          render: (_: unknown, item: PatientPortalAppointmentRequest) =>
            getCurrentUserVisitorRef(item, userId)?.appointmentType?.name ?? null,
          exportValue: (item: PatientPortalAppointmentRequest) =>
            getCurrentUserVisitorRef(item, userId)?.appointmentType?.name ?? '',
        };
        }),
    [columns, userId],
  );
}

export function PatientPortalAppointmentRequestsListWidget(props: AppointmentRequestListWidgetProps) {
  const {
    columns: columnsSettings,
    from,
    to,
    status,
    visitorIds,
    defaultFilterRule,
    onTableStateChange,
    actions: propsActions,
  } = props;
  const userId = useUserContextSelector((x) => x.id);
  const { t } = useTranslation(undefined, { keyPrefix: 'appointments.requests.list' });
  const actions = propsActions ?? (['ViewDetails', 'Cancel'] as AppointmentRequestListAction[]);
  const columns = usePatientPortalAppointmentRequestColumns(columnsSettings, actions, userId);
  const { isFetching, data } = useGetPatientPortalAppointmentRequestsQuery({
    statuses: status,
    visitorIds,
    from,
    to,
  });
  const [tableState, setTableState, searchValue, onSearchChange] = useUrlTableState(
    'appointment-request-list',
    {
      sortRule: { fieldId: 'requestedPeriod.start', dir: 'asc' },
      filterRules: [{ fieldId: 'status', operator: '=', value: AppointmentRequestStatus.Active }],
    },
  );
  const searchBy = Table.useSearchBy<PatientPortalAppointmentRequest>([(x) => x.note], []);

  const onChange = useCallback(
    (value: TableState) => {
      setTableState(value);
      onTableStateChange?.(value);
    },
    [onTableStateChange, setTableState],
  );

  return (
    <Table<PatientPortalAppointmentRequest>
      uid="appointment-request-list"
      loading={isFetching}
      columns={columns}
      dataSource={data}
      rowKey="id"
      {...tableState}
      onChange={onChange}
      searchable={searchBy}
      searchPlaceholder={t('searchPlaceholder_WITHOUT_VISITOR')}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      {...defaultFilterRule}
    />
  );
}
